import React, {
  useContext,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { bool, func, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import useStyles from 'hooks/useStyles';
import cx from 'classnames';

import ContextStore from 'utils/createContext';
import Button from 'components/Button';
import FullMask from 'components/FullMask';
import { CloseIcon } from 'components/Icon';
import GetFreeTrialLink from 'components/GetFreeTrialLink';

import { BRIGHTCOVE } from 'constants/videoSource';
import { FREE, PAID } from 'routes/live/constants/episodeGroupTypes';
import { TAB, LIST } from 'routes/live/constants/episodeListTypes';
import { EXTERNAL_SOURCE } from 'routes/live/constants/liveEpisodeState';

import SyllabusTab from '../SyllabusTab';
import EpisodeListItem from '../EpisodeListItem';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './EpisodeList.scss';

function EpisodeList({ className, showFreeTrial, onSyllabusLessonClick }) {
  useStyles(styles);

  const ref = useRef();
  const [showModal, setShowModal] = useState(false);

  const { t } = useTranslation(['live']);
  const {
    currentLesson,
    onClickLesson,
    freeLessonList,
    finishedLessons,
    upcomingLessons,
    isTopicEnrolled,
    isSemester,
    semesterExpiredDays,
    syllabuses,
    liveTopicId,
    liveTopicName,
  } = useContext(ContextStore);
  const toggleModal = () => setShowModal(modalState => !modalState);

  const groupWithEpisodes = useMemo(
    () => ({
      [FREE]: {
        title: t('live:topic.freeLessons'),
        episodes: freeLessonList,
        type: LIST,
      },
      [PAID]: {
        title: t('live:topic.episode'),
        episodes: [...finishedLessons, ...upcomingLessons],
        type: isTopicEnrolled ? LIST : TAB,
      },
    }),
    [freeLessonList, finishedLessons, upcomingLessons, isTopicEnrolled, t],
  );

  const firstUpcomingLessonIndex = finishedLessons.length;

  useEffect(() => {
    if (!ref.current) return;

    const latestEpisode = ref.current.querySelector(
      "[data-first-upcoming='true']",
    );

    if (!latestEpisode) return;

    latestEpisode.parentNode.scrollTop =
      latestEpisode.offsetTop - latestEpisode.parentNode.offsetTop;
  }, []);

  const handleClickEpisode = useCallback(
    lesson => {
      onClickLesson(lesson);
      if (showModal) toggleModal();
    },
    [onClickLesson, showModal],
  );

  const handleClickSyllabus = useCallback(
    (lessonName, syllabusName) => {
      onSyllabusLessonClick(lessonName, syllabusName);
      if (showModal) toggleModal();
    },
    [onSyllabusLessonClick, showModal],
  );

  const renderGroup = useMemo(
    () => ([groupType, { title, episodes, type }]) => {
      if (episodes.length === 0) return null;
      return type === LIST ? (
        <>
          <li key={groupType} className={styles.group}>
            <h2 className={styles.groupTitle}>{title}</h2>
            <ul className={cx(styles.list, styles[groupType])}>
              {episodes.map((lesson, lessonIndex) => (
                <EpisodeListItem
                  {...lesson}
                  key={lesson.id}
                  isSemester={isSemester}
                  isWithoutVideoDuration={
                    lesson.videoSource === BRIGHTCOVE && !lesson.videoDuration
                  }
                  isRealLive={lesson.state === EXTERNAL_SOURCE}
                  semesterExpiredDays={semesterExpiredDays}
                  ordinalNumber={lessonIndex + 1}
                  onClick={() => handleClickEpisode(lesson)}
                  isSelected={currentLesson?.id === lesson.id}
                  isPlaying={
                    currentLesson?.id === lesson.id && currentLesson?.isPlaying
                  }
                  isFirstUpcoming={
                    groupType === PAID &&
                    lessonIndex === firstUpcomingLessonIndex
                  }
                />
              ))}
            </ul>
          </li>
          <GetFreeTrialLink
            isShow={showFreeTrial}
            type="regular_class"
            id={liveTopicId}
            name={liveTopicName}
            section="topic_syllabus"
          />
        </>
      ) : (
        syllabuses.length > 0 && (
          <SyllabusTab
            currentLessonId={currentLesson?.id}
            liveTopicId={liveTopicId}
            tabPanes={syllabuses}
            className={styles.syllabuses}
            onSyllabusLessonClick={handleClickSyllabus}
            title={t('live:topic.classOutline')}
          />
        )
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      liveTopicId,
      currentLesson?.id,
      currentLesson?.isPlaying,
      handleClickEpisode,
      firstUpcomingLessonIndex,
      semesterExpiredDays,
      isSemester,
      syllabuses,
    ],
  );

  const renderList = useMemo(() => {
    if (showModal)
      return (
        <FullMask>
          <div className={styles.modalWrapper}>
            <div className={styles.modalHeader}>
              <CloseIcon
                width={24}
                height={24}
                onClick={toggleModal}
                className={styles.modalCloseIcon}
              />
            </div>
            <ul className={cx({ [styles.listReversed]: isTopicEnrolled })}>
              {Object.entries(groupWithEpisodes).map(renderGroup)}
            </ul>
          </div>
        </FullMask>
      );
    return (
      <ul
        className={cx(
          styles.wrapper,
          { [styles.listReversed]: isTopicEnrolled },
          className,
        )}
        ref={ref}
      >
        {Object.entries(groupWithEpisodes).map(renderGroup)}
      </ul>
    );
  }, [className, groupWithEpisodes, renderGroup, showModal, isTopicEnrolled]);

  return (
    <>
      <div className={styles.toggler}>
        <Button
          type="dark"
          block
          text={t('live:topic.scheduledLessons')}
          onClick={toggleModal}
        />
      </div>
      {renderList}
    </>
  );
}

EpisodeList.propTypes = {
  className: string,
  onSyllabusLessonClick: func,
  showFreeTrial: bool,
};

EpisodeList.defaultProps = {
  className: undefined,
  onSyllabusLessonClick: null,
  showFreeTrial: false,
};

export default EpisodeList;
